// Import react.
import * as React from 'react';
// Import props.
import {
	InputDataSelectionListProps,
} from '@app/props';
// Import api models.
import {
	PerilDeductible,
	PossibleDeductible,
	PossiblePeril,
} from '@app/models/api';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import enumerations.
import {
	Peril,
} from '@app/enumerations';
// Import services.
import {
	PerilService,
} from '@app/services';

interface UseInputPerilDeductibleParameters {
	id: string;
	disabled: boolean;
	required: boolean;
	selectedPerilDeductibles: PerilDeductible[];
	possiblePerils: PossiblePeril[];
	showValidation?: boolean;
}

interface UseInputPerilDeductibleResults {
	isDisabled: boolean;
	selectedPerilsDeductibles: PerilDeductible[];
	focusToPerilDeductiblesInput: (peril: Peril) => void;
	checkValidation: (focusToElement: boolean) => boolean;
	changeShowValidationFlag: (showValidation: boolean) => void;
	doPerilDeductiblesHaveChanges: () => boolean;
	applyPerilDeductibleProps: (peril: Peril) => InputDataSelectionListProps | undefined;
	getPerilDeductiblesLabelByPerilCode: (peril: Peril) => string;
}

const useInputPerilDeductible = (params: UseInputPerilDeductibleParameters): UseInputPerilDeductibleResults => {

	// #region exported methods

	/**
	 * Focuses to peril deductibles input. 
	 **/
	const focusToPerilDeductiblesInput = (peril: Peril): void => {
		if (queuedPerilIdToFocus === undefined) {
			setQueuedPerilIdToFocus(peril);
		}
	};

	/**
	 * Method to check form validation. 
	 **/
	const checkValidation = (focusToElement: boolean): boolean => {
		let areSelectedPerilDeductiblesValid: boolean = true;
		if (!params.disabled) {
			// Validate all the selected peril deductibles.
			areSelectedPerilDeductiblesValid = selectedPerilsDeductibles.every((selectedPerilDeductible: PerilDeductible) => {
				let isValid: boolean = true;
				// Get selected peril code.
				const selectedPerilCode: Peril | undefined = selectedPerilDeductible.perilCode ?? undefined;
				// Get selected deductible code.
				const selectedDeductibleCode: string | undefined = selectedPerilDeductible.deductibleCode ?? undefined;
				// Find matching possible peril based off the selected peril code.
				const possiblePeril: PossiblePeril | undefined = selectedPerilCode !== undefined
					? getPossiblePeril(selectedPerilCode)
					: undefined;

				// Validate the selected peril deductible.
				isValid = validatePerilDeductible(selectedDeductibleCode, possiblePeril?.possibleDeductibles ?? [], params.required).isValid;
				// Scroll and focus to input if invalid.
				if (!isValid
					&& focusToElement
					&& selectedPerilCode !== undefined) {
					// Focus on input.
					focusToPerilDeductiblesInput(selectedPerilCode);

				}
				return isValid;
			});
		}

		return areSelectedPerilDeductiblesValid;
	}

	/**
	 * Sets the show validation flag in the state.
	 * @param showValidation
	 */
	const changeShowValidationFlag = (showValidation: boolean): void => {
		setShowValidation(showValidation);
	}

	/**
	 * Checks to see if the perils deductibles have changes. 
	 **/
	const doPerilDeductiblesHaveChanges = (): boolean => {
		return hasChanges;
	}

	/**
	 * Applies the data selection list props for the peril deductibles.
	 * @param peril
	 */
	const applyPerilDeductibleProps = (peril: Peril): InputDataSelectionListProps | undefined => {
		let props: InputDataSelectionListProps | undefined = undefined;
		// Fetch the matching possible peril.
		let possiblePeril: PossiblePeril | undefined = getPossiblePeril(peril);
		if (possiblePeril !== undefined) {
			// Fetch current selected deductible.
			let selectedPerilDeductible: PerilDeductible | undefined = getSelectedPerilDeductible(peril);
			let selectedDeductibleCode: string | undefined = selectedPerilDeductible?.deductibleCode ?? undefined;
			// Validate input, if needed.
			let validationResult: ValidatorResponse = !params.disabled
				? validatePerilDeductible(selectedDeductibleCode, possiblePeril.possibleDeductibles, params.required)
				: new ValidatorResponse(true, String.empty());
			// Generate the element id for the peril deductibles.
			let perilDeductibleElementId: string = generateIdForPerilDeductiblesElement(peril);
			// Assign the prop values.
			props = {
				id: perilDeductibleElementId,
				label: getPerilDeductiblesLabel(possiblePeril),
				hasErrors: showValidation ? !validationResult.isValid : false,
				helperText: showValidation ? validationResult.message : undefined,
				isOptional: !params.required,
				disabled: params.disabled,
				inputRef: (ref: React.RefObject<any>) => registerInputRef(peril, ref),
				onSelect: (selectedPerilDeductible: string | undefined) => selectPerilDeductible(peril, selectedPerilDeductible),
				possibleDataSelections: PerilService.getInstance().getEligiblePossibleDeductibles(possiblePeril),
				value: selectedDeductibleCode,
			};
		}

		return props;
	}

	const getPerilDeductiblesLabelByPerilCode = (peril: Peril): string => {
		// Fetch the matching possible peril.
		let possiblePeril: PossiblePeril | undefined = getPossiblePeril(peril);
		return possiblePeril !== undefined ? getPerilDeductiblesLabel(possiblePeril) : String.empty();
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Checks to see if the selected deductible is a valid.
	 * @param selectedPerilDeductibleCode
	 * @param possibleDeductibles
	 */
	const getMatchingPossibleDeductible = (selectedPerilDeductibleCode: string | undefined, possibleDeductibles: PossibleDeductible[]): PossibleDeductible | undefined => {
		return possibleDeductibles.find((possibleDeductible: PossibleDeductible) => {
			return possibleDeductible.code === selectedPerilDeductibleCode;
		});
	}

	/**
	 * Validates the selected peril deductible.
	 * @param selectedPerilDeductible
	 * @param possiblePeril
	 * @param required
	 */
	const validatePerilDeductible = (selectedPerilDeductible: string | undefined, possibleDeductibles: PossibleDeductible[], required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required
			&& String.isNullOrWhiteSpace(selectedPerilDeductible)) {
			// Make sure there is a selected deductible.
			response = new ValidatorResponse(false, 'A deductible must be selected.');
		}
		else {
			let matchingPossibleDeductible: PossibleDeductible | undefined = getMatchingPossibleDeductible(selectedPerilDeductible, possibleDeductibles);
			if (matchingPossibleDeductible === undefined) {
				response = new ValidatorResponse(false, 'The selected deductible is invalid.');
			}
			else if (!matchingPossibleDeductible.isEligible){
				response = new ValidatorResponse(false, PerilService.getInstance().getIneligibilityReasonsMessageForPossibleDeductible(matchingPossibleDeductible));
			}
		}

		return response;
	}

	/**
	 * Finds a matching possible peril record based on the peril code.
	 * @param peril
	 */
	const getPossiblePeril = (peril: Peril): PossiblePeril | undefined => {
		return params.possiblePerils.find((possiblePeril: PossiblePeril) => possiblePeril.code === peril);
	}

	/**
	 * Fetches the selected peril deductible by peril code.
	 * @param peril
	 */
	const getSelectedPerilDeductible = (peril: Peril): PerilDeductible | undefined => {
		return selectedPerilsDeductibles.find((perilDeductible: PerilDeductible) => perilDeductible.perilCode === peril);
	}

	/**
	 *  Fetches the label for the peril deductibles.
	 */
	const getPerilDeductiblesLabel = (possiblePeril: PossiblePeril): string => {
		return `${possiblePeril.displayName} Deductible`;
	}

	/**
	 * Saves the selected peril deductible to the state.
	 * @param peril
	 * @param selectedPerilDeductible
	 */
	const selectPerilDeductible = (peril: Peril, newSelectedPerilDeductible: string | undefined): void => {
		// Updated the selected deductible.
		setSelectedPerilsDeductibles((currentSelectedPerilsDeductibles: PerilDeductible[]) => {
			// Create copy of the current selected perils deductibles.
			let copiedSelectedPerilsDeductibles: PerilDeductible[] = [...currentSelectedPerilsDeductibles];
			// For each selected peril deductible.
			return copiedSelectedPerilsDeductibles.map((copiedSelectedPerilDeductible: PerilDeductible) => {
				if (copiedSelectedPerilDeductible.perilCode === peril) {
					copiedSelectedPerilDeductible.deductibleCode = newSelectedPerilDeductible ?? null;
				}
				return copiedSelectedPerilDeductible;
			});
		});
		// Any updates should flag the inputs of having changes.
		setHasChanges(true);
	}

	/**
	 * Registers the input ref.
	 * @param peril
	 * @param ref
	 */
	const registerInputRef = (peril: Peril, ref: React.RefObject<any>): void => {
		let inputRef: React.RefObject<any> | undefined = inputRefs.get(peril);
		if (inputRef === undefined
			|| inputRef.current === null) {
			setInputRefs((currentInputRefs: Map<Peril, React.RefObject<any>>) => {
				let copiedInputRefs: Map<Peril, React.RefObject<any>> = new Map<Peril, React.RefObject<any>>(currentInputRefs);
				copiedInputRefs.set(peril, ref);
				return copiedInputRefs;
			});
		}
	}

	// #region id generators

	/**
	 * Generates id. 
	 **/
	const generateId = (): string => {
		return params.id;
	}

	/**
	 * Generates id for peril deductibles
	 * @param possiblePeril
	 */
	const generateIdForPerilDeductiblesElement = (peril: Peril): string => {
		return `${generateId()}-perilDeductibles-${peril}`;
	}


	// #endregion id generators

	// #endregion private methods


	// #region variables

	const [selectedPerilsDeductibles, setSelectedPerilsDeductibles] = React.useState<PerilDeductible[]>(params.selectedPerilDeductibles);
	const [showValidation, setShowValidation] = React.useState<boolean>(params.showValidation ?? false);
	const [hasChanges, setHasChanges] = React.useState<boolean>(false);
	const [inputRefs, setInputRefs] = React.useState<Map<Peril, React.RefObject<any>>>(new Map<Peril, React.RefObject<any>>());
	const [queuedPerilIdToFocus, setQueuedPerilIdToFocus] = React.useState<Peril | undefined>(undefined);

	// #region cached variables

	const isDisabled: boolean = React.useMemo(() => params.disabled, [params.disabled]);

	// #endregion cached variables

	// #endregion variables


	// #region life-cycle methods.

	// Called when input ref or when the focus-to flag is updated.
	React.useEffect(() => {
		// If there is a queued input focus-to flag and the input reference is ready, then proceed to focus on the input.
		if (queuedPerilIdToFocus
			&& inputRefs.has(queuedPerilIdToFocus)) {
			// Find the matching input ref.
			let inputRef: React.RefObject<any> | undefined = inputRefs.get(queuedPerilIdToFocus);
			if (inputRef !== undefined
				&& inputRef.current !== null) {
				// If input is already focused, then do not attempt to do it again.
				if (document.activeElement !== inputRef.current) {
					inputRef.current.focus();
				}
				// Reset the queued input to focus.
				setQueuedPerilIdToFocus(undefined);
			}
		}
	}, [queuedPerilIdToFocus, inputRefs]);

	// #endregion life-cycle methods.


	return {
		isDisabled,
		selectedPerilsDeductibles,
		focusToPerilDeductiblesInput,
		checkValidation,
		changeShowValidationFlag,
		doPerilDeductiblesHaveChanges,
		applyPerilDeductibleProps,
		getPerilDeductiblesLabelByPerilCode,
	};
}

// Export parameters.
export {
	UseInputPerilDeductibleParameters,
}

// Export results.
export {
	UseInputPerilDeductibleResults,
}

export default useInputPerilDeductible;